import React, { useState, useEffect } from 'react';
import { Plus, Search, FileText, User, X, Trash2 } from 'lucide-react';
import IfadaPaymentModal from './modals/IfadaPaymentModal';
import ConfirmDialog from './modals/ConfirmDialog';
import { Gate } from './AccessStudio';

const Ifada = ({ apiCall, selectedYear, availableYears = [], hasRole, showSuccess, showError }) => {
  const [orders, setOrders] = useState([]);
  const [students, setStudents] = useState([]);
  const [pricing, setPricing] = useState([]); // ✅ NEW: Store pricing from database
  const [loading, setLoading] = useState(true);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false); // ✅ NEW
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null); // ✅ NEW

  // Admin-only order delete. Orders are corrected by deleting and
  // re-creating rather than edited line by line.
  const [deleteOrderTarget, setDeleteOrderTarget] = useState(null);
  const [deletingOrder, setDeletingOrder] = useState(false);
  
  // Order creation states
  const [orderType, setOrderType] = useState('current'); // 'current' or 'manual'
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentSearchTerm, setStudentSearchTerm] = useState('');
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  // Which academic year's students the picker searches — lets you pick a real
  // 25-26 student (who has an academic record) instead of a manual entry.
  const [studentYear, setStudentYear] = useState(selectedYear);
  const [manualName, setManualName] = useState('');
  const [manualClass, setManualClass] = useState('');
  const [manualYear, setManualYear] = useState('');
  const [orderItems, setOrderItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [ordersData, studentsData, pricingData] = await Promise.all([
        apiCall(`/ifada/orders`),
        // Students with a record for the selected year (works for past years too,
        // unlike /students which is pinned to the current year); alumni go manual.
        apiCall(`/students-for-year?academicYear=${selectedYear}`),
        apiCall(`/ifada/pricing`) // ✅ NEW: Load pricing
      ]);
      setOrders(ordersData || []);
      setStudents(studentsData || []);
      setPricing(pricingData || []); // ✅ NEW: Store pricing
    } catch (error) {
      console.error('Error loading data:', error);
      showError('Error loading data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Generate years from 1975 to current year
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= 1975; i--) {
      const startYear = i.toString().slice(-2);
      const endYear = (i + 1).toString().slice(-2);
      years.push(`${startYear}-${endYear}`);
    }
    return years;
  };

  const getCurrentAcademicYear = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    
    if (month >= 9) {
      return `${year.toString().slice(-2)}-${(year + 1).toString().slice(-2)}`;
    } else {
      return `${(year - 1).toString().slice(-2)}-${year.toString().slice(-2)}`;
    }
  };

  // ✅ NEW: Map frontend item types to database DocumentType
  const getDocumentType = (itemType) => {
    const mapping = {
      'completion_statement': 'Statement',
      'followup_statement': 'Statement',
      'grades': 'Grade Certificate',
      'University_Documents': 'University_Documents',
      'Difficulty_Process' : 'Difficulty_Process',
      'Appreciation_BF' : 'Appreciation_BF',
      'Conduct': 'Conduct'
      
    };
    return mapping[itemType] || 'Statement';
  };

  // ✅ UPDATED: Get price from database based on document type and year (3-TIER PRICING)
  const getPriceForItem = (itemType, year) => {
    const documentType = getDocumentType(itemType);
    const currentYear = getCurrentAcademicYear();
    
    // Determine year type (Current / Historical / Pre2020)
    let yearType;
    if (year === currentYear) {
      yearType = 'Current';
    } else {
      // Parse the year to determine if it's pre-2020
      const yearNum = parseInt(year.split('-')[0]);
      const fullYear = yearNum < 50 ? 2000 + yearNum : 1900 + yearNum;
      
      if (fullYear < 2020) {
        yearType = 'Pre2020';  // 19-20 and below = 1,800,000 ل.ل
      } else {
        yearType = 'Historical';  // 20-21 to 23-24 = 500,000 ل.ل
      }
    }
    
    // Find matching price in pricing array
    const priceEntry = pricing.find(p => 
      p.DocumentType === documentType && 
      p.YearType === yearType && 
      p.IsActive
    );
    
    if (priceEntry) {
      return {
        type: yearType === 'Current' ? 'Current' : yearType === 'Pre2020' ? 'Pre-2020' : 'Historical',
        price: priceEntry.Price
      };
    }
    
    // Fallback to default prices if not found in database
    console.warn(`Price not found for ${documentType} - ${yearType}, using default`);
    const defaultPrices = {
      'Current': 200000,
      'Historical': 500000,
      'Pre2020': 1800000
    };
    
    return {
      type: yearType === 'Current' ? 'Current' : yearType === 'Pre2020' ? 'Pre-2020' : 'Historical',
      price: defaultPrices[yearType] || 200000
    };
  };

  const formatLBP = (amount) => {
    return new Intl.NumberFormat('en-US').format(amount) + ' ل.ل';
  };

  // Delete the whole order. The backend refuses while a live payment stands
  // against it, so a paid order must be voided on the Payments screen first.
  const handleDeleteOrder = async () => {
    if (!deleteOrderTarget) return;
    try {
      setDeletingOrder(true);
      await apiCall(`/ifada/orders/${deleteOrderTarget.OrderID}`, { method: 'DELETE' });
      showSuccess(`Order #${deleteOrderTarget.OrderID} deleted`);
      setDeleteOrderTarget(null);
      loadData();
    } catch (error) {
      showError('Error deleting order: ' + error.message);
    } finally {
      setDeletingOrder(false);
    }
  };

  const getFilteredStudents = () => {
    if (!studentSearchTerm) return [];
    return students.filter(s => 
      `${s.FirstName} ${s.LastName}`.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
      s.StudentID.toLowerCase().includes(studentSearchTerm.toLowerCase()) ||
      s.FamilyID.toLowerCase().includes(studentSearchTerm.toLowerCase())
    ).slice(0, 10);
  };

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setStudentSearchTerm(`${student.FirstName} ${student.LastName}`);
    setShowStudentDropdown(false);
  };

  const addOrderItem = (itemType) => {
    const newItem = {
      id: Date.now(),
      itemType: itemType,
      academicYear: '',
      quantity: 1, // ✅ NEW: Default quantity
      pricing: null
    };
    setOrderItems([...orderItems, newItem]);
  };

  const updateItemYear = (itemId, year) => {
    setOrderItems(orderItems.map(item => {
      if (item.id === itemId) {
        // ✅ Get price from database based on item type and year
        const pricingInfo = getPriceForItem(item.itemType, year);
        
        return {
          ...item,
          academicYear: year,
          pricing: pricingInfo
        };
      }
      return item;
    }));
  };

  // ✅ NEW: Update item quantity
  const updateItemQuantity = (itemId, quantity) => {
    const qty = parseInt(quantity) || 1;
    if (qty < 1) return; // Prevent negative or zero quantities
    
    setOrderItems(orderItems.map(item => 
      item.id === itemId ? { ...item, quantity: qty } : item
    ));
  };

  const removeOrderItem = (itemId) => {
    setOrderItems(orderItems.filter(item => item.id !== itemId));
  };

  const calculateTotal = () => {
    return orderItems.reduce((sum, item) => {
      const itemTotal = (item.pricing?.price || 0) * (item.quantity || 1);
      return sum + itemTotal;
    }, 0);
  };

  const handleCreateOrder = async () => {
    try {
      // Validation
      if (orderType === 'current' && !selectedStudent) {
        showError('Please select a student');
        return;
      }
      if (orderType === 'manual' && (!manualName || !manualClass || !manualYear)) {
        showError('Please fill in all student details');
        return;
      }
      if (orderItems.length === 0) {
        showError('Please add at least one item');
        return;
      }
      if (orderItems.some(item => !item.academicYear)) {
        showError('Please select year for all items');
        return;
      }

      const orderData = {
        familyId: orderType === 'current' ? selectedStudent.FamilyID : null,
        studentId: orderType === 'current' ? selectedStudent.StudentID : null,
        isManualEntry: orderType === 'manual',
        manualStudentName: orderType === 'manual' ? manualName : null,
        manualClass: orderType === 'manual' ? manualClass : null,
        manualYear: orderType === 'manual' ? manualYear : null,
        items: orderItems.map(item => ({
          itemType: item.itemType,
          academicYear: item.academicYear,
          quantity: item.quantity || 1,
          unitPrice: item.pricing.price, // ✅ Unit price per item
          amount: item.pricing.price * (item.quantity || 1) // ✅ Total amount
        }))
      };

      console.log('Creating order with data:', orderData);
      const result = await apiCall("/ifada/orders", { method: "POST", body: JSON.stringify(orderData) });
      console.log('Order created:', result);
      
      showSuccess('Order created successfully');
      resetOrderForm();
      setShowOrderModal(false);
      loadData();
    } catch (error) {
      console.error('Error creating order:', error);
      showError('Error creating order: ' + error.message);
    }
  };

  const resetOrderForm = () => {
    setOrderType('current');
    setSelectedStudent(null);
    setStudentSearchTerm('');
    setManualName('');
    setManualClass('');
    setManualYear('');
    setOrderItems([]);
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await apiCall(`/ifada/orders/${orderId}/status`, { method: "PUT", body: JSON.stringify({ status: newStatus }) });
      showSuccess('Order status updated');
      loadData();
    } catch (error) {
      showError('Error updating status: ' + error.message);
    }
  };

  const getFilteredOrders = () => {
    let filtered = orders;
    
    if (searchTerm) {
      filtered = filtered.filter(order => 
        order.StudentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        order.OrderID.toString().includes(searchTerm) ||
        (order.StudentClass && order.StudentClass.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.Status === statusFilter);
    }
    
    return filtered;
  };

  const getItemTypeLabel = (itemType) => {
    const labels = {
      'completion_statement': 'إفادة إنهاء',
      'followup_statement': 'إفادة متابعة',
      'University_Documents': 'ملف جامعي',
      'Difficulty_Process': 'إنجاز ملف الصعوبات التعلمية',
      'Conduct': 'إفادة حسن سلوك',
      'Appreciation_BF': 'Appreciation BF',
      'grades': 'علامات مدرسيّة'
       
    };
    return labels[itemType] || itemType;
  };

  // ✅ NEW: Load and show order details
  const handleViewOrderDetails = async (order) => {
    try {
      const details = await apiCall(`/ifada/orders/${order.OrderID}`);
      setSelectedOrderDetails(details);
      setShowOrderDetailsModal(true);
    } catch (error) {
      showError('Error loading order details: ' + error.message);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Ifada Orders / طلبات الإفادات</h2>
            <p className="text-gray-600">Manage statements and grade certificates</p>
          </div>
          <Gate permission="ifada.orders.create" label="New Order">
            <button
              onClick={() => setShowOrderModal(true)}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <Plus size={18} />
              New Order
            </button>
          </Gate>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by student name, order #, or class..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="all">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Ready">Ready</option>
            <option value="Delivered">Delivered</option>
          </select>
        </div>

        {/* Orders Table */}
        <div className="overflow-x-auto relative">
          <table className="w-full relative">
            <thead>
              <tr className="bg-gray-100 border-b">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Order #</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Student</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Class</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Items</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Amount</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Payment</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">Delete</th>
              </tr>
            </thead>
            <tbody className="relative">
              {getFilteredOrders().map(order => (
                <tr 
                  key={order.OrderID} 
                  className="border-b hover:bg-purple-50 transition-colors"
                >
                  <td className="px-4 py-3 font-semibold cursor-pointer" onClick={() => handleViewOrderDetails(order)}>#{order.OrderID}</td>
                  <td className="px-4 py-3 cursor-pointer" onClick={() => handleViewOrderDetails(order)}>{new Date(order.OrderDate).toLocaleDateString()}</td>
                  <td className="px-4 py-3 cursor-pointer" onClick={() => handleViewOrderDetails(order)}>
                    <div className="font-medium">{order.StudentName}</div>
                    {order.IsManualEntry && (
                      <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">Manual Entry</span>
                    )}
                  </td>
                  <td className="px-4 py-3 cursor-pointer" onClick={() => handleViewOrderDetails(order)}>{order.StudentClass || 'N/A'}</td>
                  <td className="px-4 py-3 text-sm cursor-pointer" onClick={() => handleViewOrderDetails(order)}>{order.ItemCount} item(s)</td>
                  <td className="px-4 py-3 font-bold text-purple-600 cursor-pointer" onClick={() => handleViewOrderDetails(order)}>{formatLBP(order.TotalAmount)}</td>
                  <td className="px-4 py-3 cursor-pointer" onClick={() => handleViewOrderDetails(order)}>
                    <span className={`px-2 py-1 rounded text-xs ${
                      order.PaymentStatus === 'Paid' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {order.PaymentStatus}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {hasRole && hasRole('GlobalAdmin') ? (
                      <select
                        value={order.Status || order.OrderStatus || 'Processing'}
                        onChange={(e) => handleUpdateStatus(order.OrderID, e.target.value)}
                        className={`px-2 py-1 rounded text-xs border cursor-pointer ${
                          (order.Status || order.OrderStatus) === 'Delivered' ? 'bg-green-100 text-green-800' :
                          (order.Status || order.OrderStatus) === 'Ready' ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        <option value="Processing">Processing</option>
                        <option value="Pending">Pending</option>
                        <option value="Ready">Ready</option>
                        <option value="Delivered">Delivered</option>
                      </select>
                    ) : (
                      <span className={`px-2 py-1 rounded text-xs ${
                        (order.Status || order.OrderStatus) === 'Delivered' ? 'bg-green-100 text-green-800' :
                        (order.Status || order.OrderStatus) === 'Ready' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {order.Status || order.OrderStatus || 'Processing'}
                      </span>
                    )}
                  </td>
                  {/* Admin-only. Orders are corrected by deleting and
                      re-creating. A paid order must have its payment voided
                      first — the button stays disabled until then. */}
                  <td className="px-4 py-3 text-center">
                    {hasRole && hasRole('GlobalAdmin') && (
                      <button
                        onClick={() => setDeleteOrderTarget(order)}
                        disabled={order.PaymentStatus === 'Paid'}
                        className="text-red-600 hover:text-red-800 disabled:text-gray-300 disabled:cursor-not-allowed"
                        title={order.PaymentStatus === 'Paid'
                          ? 'Paid — void the payment first, then delete'
                          : 'Delete this order'}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {getFilteredOrders().length === 0 && (
                <tr>
                  <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                    No orders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Order Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-xl font-bold">Create New Order</h3>
              <button onClick={() => { setShowOrderModal(false); resetOrderForm(); }} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {/* Order Type Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Student Type</label>
                <div className="flex gap-4">
                  <button
                    onClick={() => setOrderType('current')}
                    className={`flex-1 p-4 border-2 rounded-lg ${
                      orderType === 'current' ? 'border-purple-600 bg-purple-50' : 'border-gray-300'
                    }`}
                  >
                    <User className="mx-auto mb-2" size={24} />
                    <div className="font-semibold">Current Student</div>
                    <div className="text-sm text-gray-600">Search from database</div>
                  </button>
                  <button
                    onClick={() => setOrderType('manual')}
                    className={`flex-1 p-4 border-2 rounded-lg ${
                      orderType === 'manual' ? 'border-purple-600 bg-purple-50' : 'border-gray-300'
                    }`}
                  >
                    <FileText className="mx-auto mb-2" size={24} />
                    <div className="font-semibold">Previous Student (Manual)</div>
                    <div className="text-sm text-gray-600">Enter details manually</div>
                  </button>
                </div>
              </div>

              {/* Current Student Search */}
              {orderType === 'current' && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium">Search Student</label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Year</span>
                      <select
                        value={studentYear}
                        onChange={async (e) => {
                          const y = e.target.value;
                          setStudentYear(y);
                          setSelectedStudent(null);
                          setStudentSearchTerm('');
                          try { setStudents(await apiCall(`/students-for-year?academicYear=${y}`) || []); }
                          catch (err) { showError('Error loading students: ' + err.message); }
                        }}
                        className="border rounded-lg px-2 py-1 text-sm"
                      >
                        {[...new Set([selectedYear, ...availableYears, studentYear].filter(Boolean))].map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      value={studentSearchTerm}
                      onChange={(e) => {
                        setStudentSearchTerm(e.target.value);
                        setShowStudentDropdown(true);
                        setSelectedStudent(null);
                      }}
                      onFocus={() => setShowStudentDropdown(true)}
                      placeholder="Type student name, ID, or family ID..."
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                    {showStudentDropdown && studentSearchTerm && (
                      <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {getFilteredStudents().length > 0 ? (
                          getFilteredStudents().map(student => (
                            <div
                              key={student.StudentID}
                              onClick={() => handleSelectStudent(student)}
                              className="p-3 hover:bg-purple-50 cursor-pointer border-b last:border-b-0"
                            >
                              <div className="font-medium">{student.FirstName} {student.LastName}</div>
                              <div className="text-sm text-gray-600">
                                ID: {student.StudentID} | Family: {student.FamilyID} | Class: {student.Class || 'N/A'}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-3 text-gray-500 text-center">No students found</div>
                        )}
                      </div>
                    )}
                  </div>
                  {selectedStudent && (
                    <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800">
                        ✓ Selected: {studentSearchTerm} - Class: {selectedStudent.Class || 'N/A'} ({selectedStudent.AcademicYear})
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Manual Entry Fields */}
              {orderType === 'manual' && (
                <div className="grid md:grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium mb-2">Student Name</label>
                    <input
                      type="text"
                      value={manualName}
                      onChange={(e) => setManualName(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Class</label>
                    <input
                      type="text"
                      value={manualClass}
                      onChange={(e) => setManualClass(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="e.g., Grade 12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Last Attended Year</label>
                    <input
                      type="text"
                      value={manualYear}
                      onChange={(e) => setManualYear(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="e.g., 20-21"
                    />
                  </div>
                </div>
              )}

              {/* Add Items Section */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Add Items</label>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => addOrderItem('completion_statement')}
                    className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg"
                  >
                    + إفادة إنهاء
                  </button>
                  <button
                    onClick={() => addOrderItem('followup_statement')}
                    className="px-4 py-2 bg-green-100 hover:bg-green-200 text-green-800 rounded-lg"
                  >
                    + إفادة متابعة
                  </button>
                  <button
                    onClick={() => addOrderItem('grades')}
                    className="px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-800 rounded-lg"
                  >
                    + شهادة علامات
                  </button>                  
                  <button
                    onClick={() => addOrderItem('University_Documents')}
                    className="px-4 py-2 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 rounded-lg"
                  >
                    + ملف جامعي
                  </button>
                  <button
                    onClick={() => addOrderItem('Conduct')}
                    className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-800 rounded-lg"
                  >
                    + إفادة حسن سلوك
                  </button>
                  <button
                    onClick={() => addOrderItem('Difficulty_Process')}
                    className="px-4 py-2 bg-orange-100 hover:bg-orange-200 text-orange-800 rounded-lg"
                  >
                    + إنجاز ملف الصعوبات التعلمية
                  </button>
                 <button
                    onClick={() => addOrderItem('Appreciation_BF')}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg"
                  >
                    + Appreciation BF
                  </button>
                </div>
              </div>

              {/* Order Items List */}
              {orderItems.length > 0 && (
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2">Order Items</label>
                  <div className="space-y-3">
                    {orderItems.map((item) => (
                      <div key={item.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <span className="font-medium">{getItemTypeLabel(item.itemType)}</span>
                        </div>
                        <select
                          value={item.academicYear}
                          onChange={(e) => updateItemYear(item.id, e.target.value)}
                          className="px-3 py-2 border rounded-lg"
                        >
                          <option value="">Select Year</option>
                          {generateYears().map(year => (
                            <option key={year} value={year}>{year}</option>
                          ))}
                        </select>
                        {/* ✅ NEW: Quantity Input */}
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-600">Qty:</label>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity || 1}
                            onChange={(e) => updateItemQuantity(item.id, e.target.value)}
                            className="w-16 px-2 py-2 border rounded-lg text-center"
                          />
                        </div>
                        <div className="w-32 text-right">
                          {item.pricing ? (
                            <div>
                              <div className="text-sm text-gray-600">{item.pricing.type}</div>
                              <div className="font-bold text-purple-600">{formatLBP(item.pricing.price * (item.quantity || 1))}</div>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-400">Select year</div>
                          )}
                        </div>
                        <button
                          onClick={() => removeOrderItem(item.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Total */}
              {orderItems.length > 0 && (
                <div className="p-4 bg-purple-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold">Total Amount:</span>
                    <span className="text-2xl font-bold text-purple-600">{formatLBP(calculateTotal())}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t flex gap-3 justify-end">
              <button
                onClick={() => { setShowOrderModal(false); resetOrderForm(); }}
                className="px-6 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOrder}
                disabled={orderItems.length === 0 || orderItems.some(item => !item.academicYear)}
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedOrder && (
        <IfadaPaymentModal
          order={selectedOrder}
          onClose={() => { setShowPaymentModal(false); setSelectedOrder(null); }}
          onPaymentSuccess={(paymentId) => {
            setShowPaymentModal(false);
            setSelectedOrder(null);
            loadData();
          }}
          apiCall={apiCall}
          selectedYear={selectedYear}
          showError={showError}
          showSuccess={showSuccess}
        />
      )}

      {/* ✅ NEW: Order Details Modal */}
      {showOrderDetailsModal && selectedOrderDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <div>
                <h3 className="text-xl font-bold text-gray-800">Order Details</h3>
                <p className="text-sm text-gray-600">Order #{selectedOrderDetails.OrderID}</p>
              </div>
              <button 
                onClick={() => { setShowOrderDetailsModal(false); setSelectedOrderDetails(null); }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Student Info */}
              <div className="mb-6 p-4 bg-purple-50 rounded-lg">
                <h4 className="font-bold text-purple-900 mb-3">Student Information</h4>
                <div className="grid md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Name:</span>
                    <span className="ml-2 font-semibold">{selectedOrderDetails.StudentName}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Class:</span>
                    <span className="ml-2 font-semibold">{selectedOrderDetails.StudentClass || 'N/A'}</span>
                  </div>
                  {selectedOrderDetails.FamilyID && (
                    <div>
                      <span className="text-gray-600">Family ID:</span>
                      <span className="ml-2 font-semibold">{selectedOrderDetails.FamilyID}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-600">Order Date:</span>
                    <span className="ml-2 font-semibold">{new Date(selectedOrderDetails.CreatedAt).toLocaleDateString()}</span>
                  </div>
                </div>
                {selectedOrderDetails.IsManualEntry && (
                  <div className="mt-2">
                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Manual Entry</span>
                  </div>
                )}
              </div>

              {/* Order Items */}
              <div className="mb-6">
                <h4 className="font-bold text-gray-900 mb-3">Order Items</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Type</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold">Year</th>
                        <th className="px-4 py-2 text-center text-sm font-semibold">Qty</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold">Unit Price</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrderDetails.items.map((item, index) => {
                        const unitPrice = item.UnitPrice || (item.Amount / (item.Quantity || 1));
                        return (
                          <tr key={index} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-3">{getItemTypeLabel(item.ItemType)}</td>
                            <td className="px-4 py-3">{item.AcademicYear}</td>
                            <td className="px-4 py-3 text-center font-semibold">{item.Quantity || 1}</td>
                            <td className="px-4 py-3 text-right">{formatLBP(unitPrice)}</td>
                            <td className="px-4 py-3 text-right font-bold text-purple-600">{formatLBP(item.Amount)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-purple-50">
                      <tr>
                        <td colSpan="4" className="px-4 py-3 text-right font-bold">Total Amount:</td>
                        <td className="px-4 py-3 text-right font-bold text-xl text-purple-600">
                          {formatLBP(selectedOrderDetails.TotalAmount)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Payment Status */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-3">Payment & Status</h4>
                <div className="grid md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Payment Status:</span>
                    <span className={`ml-2 px-2 py-1 rounded text-xs font-semibold ${
                      selectedOrderDetails.PaymentStatus === 'Paid' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedOrderDetails.PaymentStatus}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Order Status:</span>
                    <span className={`ml-2 px-2 py-1 rounded text-xs font-semibold ${
                      selectedOrderDetails.OrderStatus === 'Delivered' ? 'bg-green-100 text-green-800' :
                      selectedOrderDetails.OrderStatus === 'Ready' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {selectedOrderDetails.OrderStatus}
                    </span>
                  </div>
                  {selectedOrderDetails.PaymentDate && (
                    <div>
                      <span className="text-gray-600">Payment Date:</span>
                      <span className="ml-2 font-semibold">{new Date(selectedOrderDetails.PaymentDate).toLocaleDateString()}</span>
                    </div>
                  )}
                  {selectedOrderDetails.PaymentMethod && (
                    <div>
                      <span className="text-gray-600">Payment Method:</span>
                      <span className="ml-2 font-semibold">{selectedOrderDetails.PaymentMethod}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              {selectedOrderDetails.Notes && (
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h4 className="font-bold text-yellow-900 mb-2">Notes</h4>
                  <p className="text-sm text-yellow-800">{selectedOrderDetails.Notes}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t bg-gray-50 flex gap-3 justify-end">
              <button
                onClick={() => { setShowOrderDetailsModal(false); setSelectedOrderDetails(null); }}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Close
              </button>
              {selectedOrderDetails.PaymentStatus === 'Unpaid' && (
                <button
                  onClick={() => {
                    setShowOrderDetailsModal(false);
                    setSelectedOrder(selectedOrderDetails);
                    setShowPaymentModal(true);
                  }}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                >
                  Record Payment
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Admin order delete */}
      <ConfirmDialog
        open={!!deleteOrderTarget}
        tone="danger"
        title={deleteOrderTarget ? `Delete order #${deleteOrderTarget.OrderID}` : ''}
        message={deleteOrderTarget
          ? `Delete this order for ${deleteOrderTarget.StudentName
              || deleteOrderTarget.ManualStudentName
              || deleteOrderTarget.FamilyID
              || 'this student'}?`
          : ''}
        details={deleteOrderTarget
          ? `The order and all of its items are removed permanently (${formatLBP(deleteOrderTarget.TotalAmount)}). `
            + 'If this was a mistake, create a new order instead. This cannot be undone.'
          : ''}
        confirmLabel="Delete order"
        cancelLabel="Keep it"
        busy={deletingOrder}
        onConfirm={handleDeleteOrder}
        onCancel={() => setDeleteOrderTarget(null)}
      />
    </div>
  );
};

export default Ifada;